import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  ApproveResponse,
  PreviewResponse,
  RepromoteResult,
  TransferPreviewSession,
  distributionPreviewService,
} from "../services/distributionPreviewService";

/**
 * Hooks para el ciclo de revisión previa antes de crear pickings en
 * Odoo 18 producción. Plan 2026_04_25_plan_distribucion_preview §5.2.
 *
 * `useCreatePreview`  — POST que arma el snapshot.
 * `usePreview(id)`    — GET con polling 60s (refresca countdown TTL).
 * `useApprovePreview` — POST que aplica exclusiones e invalida pending.
 * `useCancelPreview`  — POST que cierra la session sin tocar Odoo.
 */
export function useCreatePreview(): {
  mutateAsync: (input: {
    store?: string;
    request_id?: string;
    force_override?: boolean;
  }) => Promise<PreviewResponse>;
  isPending: boolean;
} {
  const mutation = useMutation({
    mutationFn: (input: { store?: string; request_id?: string; force_override?: boolean }) =>
      distributionPreviewService.createPreview(input),
  });
  return {
    mutateAsync: (input) => mutation.mutateAsync(input),
    isPending: mutation.isPending,
  };
}

export function usePreview(id: string | undefined): {
  data: PreviewResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const query = useQuery({
    queryKey: ["distribution", "preview", id],
    queryFn: () => {
      if (!id) throw new Error("preview id requerido");
      return distributionPreviewService.getPreview(id);
    },
    enabled: !!id,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}

export function useApprovePreview(): {
  mutateAsync: (input: {
    id: string;
    excluded_recommendation_ids?: string[];
  }) => Promise<ApproveResponse>;
  isPending: boolean;
} {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({
      id,
      excluded_recommendation_ids,
    }: {
      id: string;
      excluded_recommendation_ids?: string[];
    }) => {
      const body: { excluded_recommendation_ids?: string[] } = {};
      if (excluded_recommendation_ids) {
        body.excluded_recommendation_ids = excluded_recommendation_ids;
      }
      return distributionPreviewService.approvePreview(id, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["distribution", "pending-by-store"],
      });
      void qc.invalidateQueries({
        queryKey: ["distribution", "transfer-orders"],
      });
    },
  });
  return {
    mutateAsync: (input) => mutation.mutateAsync(input),
    isPending: mutation.isPending,
  };
}

/**
 * Auditoría 2026-05-05 — re-promueve líneas diferidas (por capacity cap)
 * desde el dialog. Invalida el preview para refrescar el snapshot.
 */
export function useRepromoteDeferred(): {
  mutateAsync: (input: {
    id: string;
    deferred_recommendation_ids: string[];
  }) => Promise<RepromoteResult>;
  isPending: boolean;
} {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({
      id,
      deferred_recommendation_ids,
    }: {
      id: string;
      deferred_recommendation_ids: string[];
    }) =>
      distributionPreviewService.repromoteDeferred(id, {
        deferred_recommendation_ids,
      }),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: ["distribution", "preview", variables.id],
      });
    },
  });
  return {
    mutateAsync: (input) => mutation.mutateAsync(input),
    isPending: mutation.isPending,
  };
}

export function useCancelPreview(): {
  mutateAsync: (id: string) => Promise<TransferPreviewSession>;
  isPending: boolean;
} {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (id: string) => distributionPreviewService.cancelPreview(id),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["distribution", "pending-by-store"],
      });
    },
  });
  return {
    mutateAsync: (id) => mutation.mutateAsync(id),
    isPending: mutation.isPending,
  };
}
